import React, { useState } from "react";
import { 
  Shield, Fuel, Wrench, Monitor, Leaf, TrendingUp, Activity,
  ChevronRight, ArrowLeft, Check, Phone, Mail, MapPin,
  Play, Users, Target, Zap, Bus, Truck, Package, Car, 
  Award, Clock, Euro, Flame, ChevronDown, Star
} from "lucide-react";

// Logo MOBILSAFE oficial
const MOBILSAFE_LOGO = "/logo-mobilsafe.png";

const MockupV2Real = () => {
  // Leer la vista desde la URL
  const getInitialVista = () => {
    const path = window.location.pathname;
    if (path.includes('/mockup-v2/caes')) return 'caes-landing';
    if (path.includes('/mockup-v2/calculadora')) return 'calculadora';
    if (path.includes('/mockup-v2/resultado')) return 'resultado';
    if (path.includes('/mockup-v2/confirmacion')) return 'confirmacion';
    if (path.includes('/mockup-v2/landing') || path === '/mockup-v2' || path === '/mockup-v2/') return 'landing';
    return 'landing'; // Por defecto muestra la landing principal
  };
  
  const [vista, setVista] = useState(getInitialVista);
  const [formData, setFormData] = useState({
    numVehicles: 50,
    kmAnuales: 45000,
    consumoMedio: 28,
    ahorroPercent: 5,
    sector: 'logistica'
  });

  // Cálculo simulado
  const litrosAnuales = (formData.numVehicles * formData.kmAnuales / 100) * formData.consumoMedio;
  const litrosAhorrados = litrosAnuales * (formData.ahorroPercent / 100);
  const kwhAhorrados = litrosAhorrados * 10;
  const mwhAhorrados = kwhAhorrados / 1000;
  const caesBruto = mwhAhorrados * 125;
  const caesEstimado = Math.round(caesBruto * 0.8);
  const rangoMin = Math.round(caesEstimado * 0.85).toLocaleString('es-ES');
  const rangoMax = Math.round(caesEstimado * 1.15).toLocaleString('es-ES');

  // ==================== SELECTOR (ya no se usa por defecto) ====================
  if (vista === 'selector') {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-8 mx-auto mb-6" />
            <h1 className="text-2xl font-black text-slate-900 mb-2">MOCKUP V2 - Estilo mobilsafe.es</h1>
            <p className="text-slate-500">Selecciona una pantalla para ver</p>
          </div>
          
          <div className="space-y-3">
            {[
              { id: 'landing', title: 'Landing Principal', desc: 'Con las 6 áreas y stats' },
              { id: 'caes-landing', title: 'Landing CAES', desc: 'Tu Ingreso CAES' },
              { id: 'calculadora', title: 'Calculadora CAES', desc: 'Formulario de cálculo' },
              { id: 'resultado', title: 'Resultado Parcial', desc: 'Rango + propuesta +50%' },
              { id: 'confirmacion', title: 'Confirmación', desc: 'Gracias por tu solicitud' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setVista(item.id)}
                className="w-full p-4 bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 rounded-xl text-left transition-all"
              >
                <h3 className="font-bold text-slate-800">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ==================== LANDING PRINCIPAL ====================
  if (vista === 'landing') {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-8" />
            <button className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold text-sm transition-colors">
              TU INGRESO CAES
            </button>
          </div>
        </header>

        {/* Hero */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-full mb-6">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-emerald-700 text-sm font-medium">SIMULADOR DE IMPACTO SOSTENIBLE</span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 leading-tight">
                REDUCE COSTES Y{" "}
                <span className="text-emerald-500">EMISIONES CO₂</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Calcula en segundos el impacto sostenible de MOBILSAFE en tu flota. 
                Ahorro de combustible, reducción de emisiones y certificados CAES monetizables.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <button className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20">
                  <Leaf className="w-5 h-5" />
                  COMENZAR SIMULACIÓN
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20">
                  <Flame className="w-5 h-5" />
                  TU INGRESO CAES
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-200">
                <div>
                  <p className="text-3xl font-black text-emerald-500">+500</p>
                  <p className="text-sm text-slate-500">FLOTAS ANALIZADAS</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-emerald-500">45K</p>
                  <p className="text-sm text-slate-500">TON CO₂ EVITADAS</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-emerald-500">12M€</p>
                  <p className="text-sm text-slate-500">AHORROS GENERADOS</p>
                </div>
              </div>
            </div>

            {/* Tarjetas de áreas */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Shield, title: "SEGURIDAD", desc: "Reduce accidentes y riesgo", color: "bg-red-500" },
                { icon: Fuel, title: "COMBUSTIBLE", desc: "Hasta 8% menos consumo", color: "bg-amber-500" },
                { icon: Activity, title: "OPERATIVA", desc: "Cumple plazos", color: "bg-blue-500" },
                { icon: Wrench, title: "MANTENIMIENTO", desc: "Anticipa averías", color: "bg-purple-500" },
                { icon: Monitor, title: "TIC / DATOS", desc: "Integra fácil", color: "bg-cyan-500" },
                { icon: Leaf, title: "SOSTENIBILIDAD", desc: "Reduce CO₂ e ingresa dinero", color: "bg-emerald-500" },
              ].map((area, i) => (
                <div 
                  key={i} 
                  className="p-5 bg-slate-50 hover:bg-white border border-slate-200 hover:border-emerald-300 rounded-xl cursor-pointer transition-all hover:shadow-lg group"
                >
                  <div className={`w-10 h-10 ${area.color} rounded-lg flex items-center justify-center mb-3 text-white`}>
                    <area.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-sm">{area.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">{area.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-6 opacity-60" />
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-emerald-600">Política de privacidad</a>
              <a href="#" className="hover:text-emerald-600">Cookies</a>
            </div>
          </div>
        </footer>

        {/* Navegación entre mockups */}
        <div className="fixed top-20 right-6 flex gap-2 z-50">
          <button onClick={() => setVista('selector')} className="px-3 py-2 bg-slate-800 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-slate-700">
            Ver todas
          </button>
          <button onClick={() => setVista('caes-landing')} className="px-3 py-2 bg-emerald-500 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-emerald-600 flex items-center gap-1">
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ==================== LANDING CAES ====================
  if (vista === 'caes-landing') {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-emerald-500" />
              <span className="text-slate-600 text-sm font-medium">CALCULA MÁS AHORROS CON</span>
              <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-6" />
            </div>
            <button className="px-5 py-2 bg-emerald-500 text-white rounded-lg font-bold text-sm">
              TU INGRESO CAES
            </button>
          </div>
        </header>

        {/* Hero CAES */}
        <section className="py-16 px-6 bg-gradient-to-br from-emerald-50 to-slate-50">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-bold mb-6">
              CERTIFICADOS DE AHORRO ENERGÉTICO
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 leading-tight">
              Tu flota tiene{" "}
              <span className="text-emerald-500">dinero dormido</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto">
              Descubre cuánto puedes ganar con los Certificados de Ahorro Energético (CAES) 
              gracias a la conducción eficiente de tu flota.
            </p>
            <button onClick={() => setVista('calculadora')} className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg shadow-emerald-500/20 flex items-center gap-3 mx-auto transition-all">
              CALCULAR MI INGRESO CAES <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </section>

        {/* Qué son los CAES */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-black text-slate-900 mb-4">¿Qué son los CAES?</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Los Certificados de Ahorro Energético son un mecanismo que permite a las empresas 
                  de transporte <strong>monetizar sus ahorros de combustible</strong> derivados de una conducción más eficiente.
                </p>
                <ul className="space-y-3">
                  {[
                    "Obligación de las petroleras de comprar ahorros",
                    "Ingresos directos por conducción eficiente",
                    "Sin inversión inicial necesaria"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-emerald-500 rounded-2xl p-8 text-white text-center">
                <div className="text-6xl font-black mb-2">+50%</div>
                <div className="text-xl font-bold mb-2">Más ingresos con MOBILSAFE</div>
                <p className="text-emerald-100 text-sm">
                  Frente a gestores que solo tramitan papeles, nosotros mejoramos tu conducción real.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciación */}
        <section className="py-16 px-6 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-black text-center text-slate-900 mb-10">
              ¿Por qué MOBILSAFE consigue más?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Users, title: "90 días de acompañamiento", desc: "Formación continua a tus conductores." },
                { icon: Target, title: "Expertos en TRA-020", desc: "La ficha más difícil pero más rentable." },
                { icon: Zap, title: "Acuerdos exclusivos", desc: "Mejor precio por tus CAES." }
              ].map((item, i) => (
                <div key={i} className="p-6 bg-white border border-slate-200 rounded-xl text-center">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 px-6 bg-emerald-600">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-black text-white mb-4">
              ¿15 minutos para explicarte cómo lo hacemos?
            </h2>
            <p className="text-emerald-100 mb-8">Sin compromiso. Te contamos nuestra metodología.</p>
            <button className="px-8 py-4 bg-white hover:bg-slate-50 text-emerald-600 rounded-xl font-bold text-lg transition-all">
              QUIERO MI REUNIÓN GRATUITA
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-slate-400 text-sm">© 2026 MOBILSAFE Fleet Technology</p>
          </div>
        </footer>

        {/* Navegación entre mockups */}
        <div className="fixed top-20 right-6 flex gap-2 z-50">
          <button onClick={() => setVista('landing')} className="px-3 py-2 bg-slate-800 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-slate-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          <button onClick={() => setVista('calculadora')} className="px-3 py-2 bg-emerald-500 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-emerald-600 flex items-center gap-1">
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ==================== CALCULADORA CAES ====================
  if (vista === 'calculadora') {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-emerald-600">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button onClick={() => setVista('caes-landing')} className="p-2 hover:bg-white/10 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Euro className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-black text-white">TU INGRESO CAES</h1>
              <p className="text-sm text-emerald-100">Certificados de Ahorro Energético</p>
            </div>
          </div>
        </header>

        {/* Calculator Form */}
        <div className="max-w-lg mx-auto px-6 py-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 leading-tight">
              ¿Cuánto dinero estás<br/>
              <span className="text-emerald-500">dejando de cobrar</span>?
            </h2>
            <p className="text-slate-500 mt-3">
              Calcula en 30 segundos lo que podrías recuperar.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-5">
            {/* Número de vehículos */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Nº de Vehículos</label>
              <input
                type="number"
                value={formData.numVehicles}
                onChange={(e) => setFormData({...formData, numVehicles: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-xl font-bold text-center text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              />
            </div>

            {/* Km anuales */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Km/año por vehículo</label>
              <input
                type="number"
                value={formData.kmAnuales}
                onChange={(e) => setFormData({...formData, kmAnuales: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-xl font-bold text-center text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              />
            </div>

            {/* Consumo medio */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Consumo medio (L/100km)</label>
              <input
                type="number"
                step="0.1"
                value={formData.consumoMedio}
                onChange={(e) => setFormData({...formData, consumoMedio: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-xl font-bold text-center text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              />
            </div>

            {/* % Ahorro */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">% Ahorro combustible</label>
              <input
                type="number"
                step="1"
                min="1"
                max="20"
                value={formData.ahorroPercent}
                onChange={(e) => setFormData({...formData, ahorroPercent: parseInt(e.target.value) || 5})}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-xl font-bold text-center text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              />
            </div>

            {/* Tipo de Flota */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">Tipo de Flota</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'urbano', label: 'Urbano', icon: Bus },
                  { value: 'interurbano', label: 'Interurbano', icon: Truck },
                  { value: 'distribucion', label: 'Distribución', icon: Package },
                  { value: 'logistica', label: 'Logística', icon: Truck },
                  { value: 'taxi', label: 'Taxi/VTC', icon: Car },
                ].map((sector) => (
                  <button
                    key={sector.value}
                    onClick={() => setFormData({...formData, sector: sector.value})}
                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                      formData.sector === sector.value 
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700' 
                        : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'
                    }`}
                  >
                    <sector.icon className="w-5 h-5" />
                    <span className="text-xs font-semibold">{sector.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Calcular */}
          <button
            onClick={() => setVista('resultado')}
            className="w-full mt-6 py-4 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-bold text-lg text-white shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
          >
            <Zap className="w-5 h-5" />
            CALCULAR MI CAES
          </button>
          
          <p className="text-center text-slate-400 text-sm mt-4">
            * Cálculo basado en el % de ahorro que indiques
          </p>
        </div>

        {/* Navegación entre mockups */}
        <div className="fixed top-20 right-6 flex gap-2 z-50">
          <button onClick={() => setVista('caes-landing')} className="px-3 py-2 bg-slate-800 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-slate-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          <button onClick={() => setVista('resultado')} className="px-3 py-2 bg-emerald-500 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-emerald-600 flex items-center gap-1">
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ==================== RESULTADO PARCIAL ====================
  if (vista === 'resultado') {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-emerald-600">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button onClick={() => setVista('calculadora')} className="p-2 hover:bg-white/10 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-black text-white">TU INGRESO CAES</h1>
              <p className="text-sm text-emerald-100">Resultado de tu cálculo</p>
            </div>
          </div>
        </header>

        <div className="max-w-lg mx-auto px-6 py-8">
          {/* Sección 1: Lo Normal */}
          <div className="mb-8">
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 bg-slate-100 border border-slate-300 px-4 py-2 rounded-full">
                <TrendingUp className="w-4 h-4 text-slate-500" />
                <span className="text-slate-600 font-semibold text-sm">ESTO ES LO NORMAL</span>
              </div>
            </div>

            <p className="text-center text-slate-600 mb-4">
              Tu flota de <span className="font-bold text-slate-800">{formData.numVehicles} vehículos</span> generaría
              con cualquier gestor CAES:
            </p>

            <div className="bg-slate-100 border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-2xl md:text-3xl font-black text-slate-500">{rangoMin} €</span>
                <span className="text-lg text-slate-400">-</span>
                <span className="text-2xl md:text-3xl font-black text-slate-500">{rangoMax} €</span>
              </div>
              <p className="text-slate-400 text-sm">en certificados CAES</p>
            </div>
          </div>

          {/* Sección 2: MOBILSAFE es diferente */}
          <div className="mb-8">
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-300 px-4 py-2 rounded-full">
                <Award className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-700 font-bold text-sm">PERO NOSOTROS NO SOMOS LO NORMAL</span>
              </div>
            </div>

            <div className="bg-emerald-500 border-2 border-emerald-400 rounded-2xl p-6 text-center text-white">
              <p className="text-emerald-100 text-lg mb-3">
                Con el método <span className="font-black text-white">MOBILSAFE</span>,
                nuestros clientes consiguen hasta un
              </p>
              <p className="text-6xl font-black mb-2">+50%</p>
              <p className="text-xl font-bold">MÁS</p>
            </div>

            {/* Razones */}
            <div className="mt-6 space-y-3">
              {[
                { icon: Users, title: "No te dejamos solo ni un día", desc: "90 días de acompañamiento real" },
                { icon: Award, title: "La ficha más difícil, pero la más rentable", desc: "Expertos en la TRA-020" },
                { icon: TrendingUp, title: "Acuerdos marco con sujetos obligados", desc: "Optimización de la venta" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-bold text-emerald-700 text-sm">{item.title}</p>
                    <p className="text-slate-500 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Formulario de contacto */}
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6">
            <h3 className="text-lg font-black text-center text-slate-800 mb-2">
              ¿Quieres saber cuánto puedes conseguir con <span className="text-emerald-600">MOBILSAFE</span>?
            </h3>
            <p className="text-slate-500 text-center mb-5 text-sm">
              Déjanos tus datos y concertamos una reunión sin compromiso.
            </p>

            <div className="space-y-4">
              <input placeholder="Nombre de la empresa" className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
              <input placeholder="Email" type="email" className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
              <input placeholder="Teléfono" type="tel" className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
            </div>

            <button
              onClick={() => setVista('confirmacion')}
              className="w-full mt-5 py-4 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2"
            >
              QUIERO MI REUNIÓN GRATUITA
            </button>
          </div>
        </div>

        {/* Navegación entre mockups */}
        <div className="fixed top-20 right-6 flex gap-2 z-50">
          <button onClick={() => setVista('calculadora')} className="px-3 py-2 bg-slate-800 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-slate-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          <button onClick={() => setVista('confirmacion')} className="px-3 py-2 bg-emerald-500 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-emerald-600 flex items-center gap-1">
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ==================== CONFIRMACIÓN ====================
  if (vista === 'confirmacion') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-4">
            ¡Solicitud recibida!
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            Nos pondremos en contacto contigo en las próximas <strong>24-48 horas</strong> para agendar tu reunión gratuita.
          </p>
          
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-emerald-800 mb-2">¿Qué pasa ahora?</h3>
            <ul className="text-left text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                Un comercial de MOBILSAFE te contactará
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                Analizaremos tu caso concreto
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                Te presentaremos una propuesta personalizada
              </li>
            </ul>
          </div>

          <button
            onClick={() => setVista('landing')}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold transition-all"
          >
            Volver al inicio
          </button>
        </div>

        {/* Navegación entre mockups */}
        <div className="fixed top-20 right-6 flex gap-2 z-50">
          <button onClick={() => setVista('resultado')} className="px-3 py-2 bg-slate-800 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-slate-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          <button onClick={() => setVista('landing')} className="px-3 py-2 bg-emerald-500 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-emerald-600 flex items-center gap-1">
            Inicio <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default MockupV2Real;
